import { useState } from 'react';
import { styled } from '@mui/material/styles';
import { UploadManagerIcon } from '../UploadManager/UploadManagerIcon';
import { DownloadManagerStatus } from './DownloadManagerStatus';
import FailedFilesModal from './FailedFilesModal';
import RefreshIcon from '@/assets/refresh-double2.svg?react';
import CancelIcon from '@/assets/cancel2.svg?react';
import IconButton from '../IconButton/IconButton';
import { DownloadStatus } from '@/contexts/DownloadContext';
import { DownloadSignedUrlFile } from '@/lib/download';

const DownloadManagerItem = ({
  status,
  title,
  progress,
  preparing,
  files,
  onRetry,
  onDismiss,
}: {
  status: DownloadStatus;
  title: string;
  progress?: number;
  preparing?: boolean;
  files?: DownloadSignedUrlFile[] | null;
  onRetry: () => void;
  onDismiss: () => void;
}) => {
  const [showFailedFiles, setShowFailedFiles] = useState(false);
  const hasFailedFiles = Boolean(files && files.length > 0);

  return (
    <Root>
      <UploadManagerIcon status={status} />
      <Description>
        <div>{title}</div>
        <DownloadManagerStatus
          status={status}
          progress={progress}
          preparing={preparing}
          failedCount={files?.length ?? null}
          onShowFailedFiles={
            hasFailedFiles ? () => setShowFailedFiles(true) : undefined
          }
        />
      </Description>
      {status === 'failed' ? (
        <IconButton onClick={onRetry} aria-label="Retry download">
          <StyledRefreshIcon />
        </IconButton>
      ) : null}
      {status !== 'pending' ? (
        <IconButton onClick={onDismiss} aria-label="Dismiss download">
          <StyledCancelIcon />
        </IconButton>
      ) : null}
      {hasFailedFiles ? (
        <FailedFilesModal
          open={showFailedFiles}
          files={files as DownloadSignedUrlFile[]}
          onClose={() => setShowFailedFiles(false)}
          onRetry={() => {
            setShowFailedFiles(false);
            onRetry();
          }}
        />
      ) : null}
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: '10px 16px',
  borderRadius: theme.radii.xxs,
  border: `1px solid ${theme.palette.grey200}`,
}));

const Description = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
}));

const StyledCancelIcon = styled(CancelIcon)(({ theme }) => ({
  path: {
    stroke: theme.palette.white,
  },
}));

const StyledRefreshIcon = styled(RefreshIcon)(({ theme }) => ({
  path: {
    stroke: theme.palette.white,
  },
}));

export default DownloadManagerItem;
